import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Copy, Check, Terminal, ShieldAlert, AlertTriangle,
  CheckCircle2, XCircle, AlertCircle, RefreshCw,
  ChevronDown, ChevronUp, Users, Eye, ClipboardList,
  Plug, FileCode, Undo2, Power
} from "lucide-react";

const API = '/api';
const BASE = window.location.origin;

export default function ExchangeSetup() {
  const [data, setData] = useState(null);
  const [validation, setValidation] = useState(null);
  const [preview, setPreview] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    connect: true, rule1: true, rule2: true, enforce: false, rollback: false
  });
  const [previewMode, setPreviewMode] = useState("resolved"); // "raw" | "resolved"
  const [pilotEmail, setPilotEmail] = useState("");

  // Pilot routing-rule expansion (multi-select + generated PowerShell)
  const [routingPilots, setRoutingPilots] = useState([]); // array of emails
  const [routingRuleName, setRoutingRuleName] = useState("SignatureForge Route to Relay - Pilot");
  const [routingPs, setRoutingPs] = useState(null); // { powershell, addresses, users }
  const [routingLoading, setRoutingLoading] = useState(false);

  const generateRoutingPs = useCallback(() => {
    if (routingPilots.length === 0) {
      toast.error("Select at least one pilot user");
      return;
    }
    setRoutingLoading(true);
    axios.post(`${API}/exchange/routing-rule-powershell`, {
      emails: routingPilots,
      rule_name: routingRuleName,
    }).then(r => {
      setRoutingPs(r.data);
      if (r.data.error) {
        toast.error(r.data.error);
      } else {
        toast.success(`Generated — ${r.data.addresses.length} addresses for ${r.data.users.filter(u => u.found).length} user(s)`);
      }
    }).catch(err => toast.error(`Failed: ${err.response?.data?.detail || err.message}`))
      .finally(() => setRoutingLoading(false));
  }, [routingPilots, routingRuleName]);

  const loadRules = useCallback((email = "") => {
    const pilotParam = email ? `&pilot_email=${encodeURIComponent(email)}` : "";
    axios.get(`${API}/exchange/transport-rules?base_url=${encodeURIComponent(BASE)}${pilotParam}`)
      .then(r => setData(r.data));
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/exchange/transport-rules?base_url=${encodeURIComponent(BASE)}`),
      axios.get(`${API}/exchange/preview?base_url=${encodeURIComponent(BASE)}`),
    ]).then(([rulesRes, previewRes]) => {
      setData(rulesRes.data);
      setUsers(previewRes.data.users || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const runValidation = useCallback(() => {
    setValidating(true);
    axios.get(`${API}/exchange/validate?base_url=${encodeURIComponent(BASE)}`)
      .then(r => { setValidation(r.data); setValidating(false); })
      .catch(() => setValidating(false));
  }, []);

  const loadPreview = useCallback((email) => {
    if (!email) { setPreview(null); return; }
    setSelectedUser(email);
    axios.get(`${API}/exchange/preview?user_email=${encodeURIComponent(email)}&base_url=${encodeURIComponent(BASE)}`)
      .then(r => setPreview(r.data))
      .catch(() => toast.error("Failed to load preview"));
  }, []);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleSection = (key) => setExpandedSections(s => ({ ...s, [key]: !s[key] }));

  const CopyBtn = ({ text, label }) => (
    <Button variant="outline" size="sm" onClick={() => copyText(text, label)}
      className="border-[#E1DFDD] rounded-sm text-xs shrink-0" data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      {copiedField === label ? <Check size={12} className="mr-1 text-[#107C10]" /> : <Copy size={12} className="mr-1" />}
      {copiedField === label ? "Copied" : "Copy"}
    </Button>
  );

  const StatusIcon = ({ status }) => {
    if (status === "pass") return <CheckCircle2 size={14} className="text-[#107C10] shrink-0" />;
    if (status === "warn") return <AlertCircle size={14} className="text-[#D83B01] shrink-0" />;
    return <XCircle size={14} className="text-[#A4262C] shrink-0" />;
  };

  if (loading) return <div className="p-8 text-sm text-[#5E5E5E]" data-testid="exchange-loading">Loading Exchange setup...</div>;
  if (!data) return <div className="p-8 text-sm text-[#A4262C]" data-testid="exchange-error">Failed to load Exchange setup data.</div>;

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="exchange-setup-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Exchange Deployment Package</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">
            Transport rules for server-side signature injection. Copy-paste into Exchange Online PowerShell.
          </p>
        </div>
        <Button onClick={runValidation} disabled={validating} variant="outline" size="sm"
          className="border-[#0F6CBD] text-[#0F6CBD] rounded-sm text-xs" data-testid="run-validation-btn">
          <RefreshCw size={12} className={`mr-1.5 ${validating ? "animate-spin" : ""}`} />
          {validating ? "Validating..." : "Run Pre-flight Check"}
        </Button>
      </div>

      {/* ── Pilot Deploy ── */}
      <div className="mt-5 bg-white border-2 border-[#0F6CBD] rounded-sm" data-testid="pilot-deploy">
        <div className="px-5 py-3 border-b border-[#0F6CBD]/20 bg-[#EBF3FC]">
          <div className="flex items-center gap-2">
            <Terminal size={16} className="text-[#0F6CBD]" />
            <h2 className="font-heading text-sm font-medium text-[#171717]">Deploy Signatures</h2>
          </div>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <label className="text-xs font-medium text-[#171717] shrink-0">Pilot user:</label>
            <select value={pilotEmail} onChange={e => { setPilotEmail(e.target.value); loadRules(e.target.value); }}
              className="text-xs border border-[#E1DFDD] rounded-sm px-2 py-1.5 bg-white text-[#171717] flex-1 max-w-sm"
              data-testid="pilot-user-select">
              <option value="">All users (full deployment)</option>
              {users.map(u => (
                <option key={u.mail} value={u.mail}>{u.displayName} ({u.mail})</option>
              ))}
            </select>
          </div>
          {pilotEmail && (
            <div className="text-xs text-[#0F6CBD] bg-[#EBF3FC] px-3 py-2 rounded-sm mb-4">
              Rules will be scoped to <strong>{pilotEmail}</strong> only. Other users are unaffected.
            </div>
          )}
          <div className="flex gap-2">
            <a href={`${API}/exchange/download-script?base_url=${encodeURIComponent(BASE)}${pilotEmail ? `&pilot_email=${encodeURIComponent(pilotEmail)}` : ''}`}
              download className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0F6CBD] text-white text-xs font-medium rounded-sm hover:bg-[#005A9E] transition-colors"
              data-testid="download-script-btn">
              <FileCode size={13} />
              Download .ps1 Script
            </a>
            <div className="text-[10px] text-[#5E5E5E] flex items-center ml-2">
              Run in Exchange Online PowerShell — creates both rules automatically
            </div>
          </div>
        </div>
      </div>

      {/* ── Pilot Routing Rule (add users with ALL aliases) ── */}
      <div className="mt-5 bg-white border-2 border-[#107C10] rounded-sm" data-testid="pilot-routing-rule">
        <div className="px-5 py-3 border-b border-[#107C10]/20 bg-[#DFF6DD]">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-[#107C10]" />
            <h2 className="font-heading text-sm font-medium text-[#171717]">Pilot Routing Rule — Expand Scope</h2>
          </div>
          <p className="text-[11px] text-[#5E5E5E] mt-1">
            Pick which users should route mail through SignatureForge. The generated PowerShell auto-includes
            every email alias for each user (e.g. both <code className="bg-white px-1 rounded">firstname.lastname@</code> AND
            <code className="bg-white px-1 rounded ml-1">surname+initial@</code>) so M365 envelope matching never misses.
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-[#171717] block mb-2">Pilot users ({routingPilots.length} selected)</label>
            <div className="border border-[#E1DFDD] rounded-sm max-h-56 overflow-y-auto bg-white" data-testid="pilot-user-checklist">
              {users.length === 0 && <div className="text-xs text-[#5E5E5E] p-3">No users loaded — run Directory Sync first.</div>}
              {users.map(u => {
                const checked = routingPilots.includes(u.mail);
                return (
                  <label key={u.mail} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-[#F5F9FE] border-b border-[#E1DFDD] last:border-b-0 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setRoutingPilots(prev => checked ? prev.filter(e => e !== u.mail) : [...prev, u.mail]);
                      }}
                      data-testid={`pilot-checkbox-${u.mail}`}
                      className="rounded-sm"
                    />
                    <span className="text-[#171717]">{u.displayName}</span>
                    <span className="text-[#5E5E5E] font-mono">{u.mail}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-[#171717] block mb-1">Rule name in Exchange</label>
              <input
                type="text"
                value={routingRuleName}
                onChange={e => setRoutingRuleName(e.target.value)}
                className="w-full text-xs border border-[#E1DFDD] rounded-sm px-2 py-1.5 bg-white text-[#171717] font-mono"
                data-testid="routing-rule-name-input"
                placeholder="SignatureForge Route to Relay - Pilot"
              />
            </div>
            <Button
              onClick={generateRoutingPs}
              disabled={routingLoading || routingPilots.length === 0}
              className="bg-[#107C10] hover:bg-[#0B5A0B] text-white text-xs"
              data-testid="generate-routing-ps-btn"
            >
              <Terminal size={12} className="mr-1.5" />
              {routingLoading ? "Generating..." : `Generate PowerShell`}
            </Button>
          </div>

          {routingPs && routingPs.powershell && (
            <div className="space-y-3" data-testid="routing-ps-output">
              <div className="bg-[#FAFAFA] border border-[#E1DFDD] rounded-sm">
                <div className="px-3 py-2 border-b border-[#E1DFDD] flex items-center justify-between">
                  <span className="text-[11px] font-medium text-[#171717]">
                    PowerShell — {routingPs.addresses.length} addresses, {routingPs.users.filter(u => u.found).length} user(s)
                  </span>
                  <CopyBtn text={routingPs.powershell} label="Routing PS" />
                </div>
                <pre className="text-[11px] text-[#171717] font-mono p-3 overflow-x-auto whitespace-pre-wrap break-all" data-testid="routing-ps-code">{routingPs.powershell}</pre>
              </div>

              {routingPs.users.some(u => !u.found) && (
                <div className="text-xs text-[#A4262C] bg-[#FDE7E9] px-3 py-2 rounded-sm" data-testid="routing-ps-not-found">
                  ⚠ Not found in directory (run Sync): {routingPs.users.filter(u => !u.found).map(u => u.input).join(", ")}
                </div>
              )}

              <details className="text-xs text-[#5E5E5E]">
                <summary className="cursor-pointer text-[#0F6CBD] hover:underline">Address breakdown per user</summary>
                <ul className="mt-2 ml-4 space-y-1">
                  {routingPs.users.filter(u => u.found).map(u => (
                    <li key={u.input}>
                      <strong className="text-[#171717]">{u.display_name || u.input}</strong>
                      <span className="ml-2 font-mono">{u.addresses.join(", ")}</span>
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      </div>

      {/* ── Pre-deployment Validator ── */}
      {validation && (
        <div className="mt-5 bg-white border border-[#E1DFDD] rounded-sm" data-testid="validation-results">
          <div className={`px-5 py-3 border-b flex items-center justify-between ${
            validation.ready ? "bg-[#DFF6DD] border-[#107C10]/20" : "bg-[#FDE7E9] border-[#A4262C]/20"
          }`}>
            <div className="flex items-center gap-2">
              {validation.ready
                ? <CheckCircle2 size={16} className="text-[#107C10]" />
                : <XCircle size={16} className="text-[#A4262C]" />}
              <span className="text-sm font-medium text-[#171717]">
                {validation.ready ? "Ready to deploy" : "Issues found — review before deploying"}
              </span>
            </div>
            <div className="flex gap-3 text-xs">
              <span className="text-[#107C10]">{validation.summary.passed} passed</span>
              {validation.summary.warnings > 0 && <span className="text-[#D83B01]">{validation.summary.warnings} warnings</span>}
              {validation.summary.failed > 0 && <span className="text-[#A4262C]">{validation.summary.failed} failed</span>}
            </div>
          </div>
          <div className="p-4 space-y-2">
            {validation.checks.map(c => (
              <div key={c.id} className="flex items-start gap-2 text-xs" data-testid={`check-${c.id}`}>
                <StatusIcon status={c.status} />
                <div>
                  <span className="font-medium text-[#171717]">{c.label}</span>
                  <span className="text-[#5E5E5E] ml-2">{c.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Prerequisites ── */}
      <div className="mt-5 bg-[#EBF3FC] border border-[#0F6CBD]/20 rounded-sm p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="text-[#0F6CBD] mt-0.5 shrink-0" />
          <div className="text-xs text-[#5E5E5E]">
            <p className="font-medium text-[#171717]">Before creating rules</p>
            <p className="mt-1">Populate sender fields in Entra/Exchange: DisplayName, Title, Company, Phone, MobilePhone, Office, WindowsEmailAddress. Empty fields render as blank.</p>
          </div>
        </div>
      </div>

      {/* ── Image Assets ── */}
      <div className="mt-5 bg-white border border-[#E1DFDD] rounded-sm p-5">
        <h2 className="font-heading text-sm font-medium text-[#171717] mb-3">Image Assets</h2>
        <div className="space-y-2 text-xs">
          {[["Logo", data.logo_url], ["Banner", data.banner_url]].map(([label, url]) => (
            <div key={label} className="flex items-center gap-3">
              <span className="text-[#5E5E5E] w-16 shrink-0">{label}:</span>
              <code className="flex-1 bg-[#F3F2F1] px-2 py-1 rounded-sm font-mono text-[10px] truncate">{url || "Not found"}</code>
              {url && <img src={url} alt={label} className="h-8 border border-[#E1DFDD] rounded-sm" />}
            </div>
          ))}
        </div>
      </div>

      {/* ── Step 0: Connect ── */}
      <ScriptSection
        num={0} color="#5C2D91" icon={<Plug size={12} />}
        title="Connect to Exchange Online"
        description="Run this first to authenticate your PowerShell session."
        script={data.powershell_connect}
        expanded={expandedSections.connect}
        onToggle={() => toggleSection("connect")}
        copyBtn={<CopyBtn text={data.powershell_connect} label="Connect script" />}
      />

      {/* ── Step 1: Short rule ── */}
      <ScriptSection
        num={1} color="#D83B01" icon={<FileCode size={12} />}
        title="Rule 1: Short Signature (Replies / Forwards)"
        description="Priority 0 — matches RE:/FW:/AW:/SV: subjects. Applies compact signature to external replies and forwards."
        script={data.powershell_short_rule}
        expanded={expandedSections.rule1}
        onToggle={() => toggleSection("rule1")}
        copyBtn={<CopyBtn text={data.powershell_short_rule} label="Short rule" />}
      />

      {/* ── Step 2: Full rule ── */}
      <ScriptSection
        num={2} color="#0F6CBD" icon={<FileCode size={12} />}
        title="Rule 2: Full Signature (New External Mail)"
        description="Priority 1 — excludes replies/forwards. Applies full branded signature to new outbound external messages."
        script={data.powershell_full_rule}
        expanded={expandedSections.rule2}
        onToggle={() => toggleSection("rule2")}
        copyBtn={<CopyBtn text={data.powershell_full_rule} label="Full rule" />}
      />

      {/* ── Step 3: Enforce ── */}
      <ScriptSection
        num={3} color="#107C10" icon={<Power size={12} />}
        title="Enable: Test → Enforce"
        description="Both rules start in TestWithoutPolicyTips. After verifying, switch to Enforce."
        script={data.powershell_test_to_enforce}
        expanded={expandedSections.enforce}
        onToggle={() => toggleSection("enforce")}
        copyBtn={<CopyBtn text={data.powershell_test_to_enforce} label="Enforce script" />}
      />

      {/* ── Rollback ── */}
      <div className="mt-3">
        <ScriptSection
          num={null} color="#A4262C" icon={<Undo2 size={12} />}
          title="Emergency Rollback"
          description="Instantly disables both rules. Mail routes normally through Exchange Online."
          script={data.powershell_rollback}
          expanded={expandedSections.rollback}
          onToggle={() => toggleSection("rollback")}
          copyBtn={<CopyBtn text={data.powershell_rollback} label="Rollback script" />}
          danger
        />
      </div>

      {/* ── Signature Preview with Real Data ── */}
      <div className="mt-6 bg-white border border-[#E1DFDD] rounded-sm" data-testid="preview-section">
        <div className="px-5 py-3 border-b border-[#E1DFDD]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye size={16} className="text-[#0F6CBD]" />
              <h2 className="font-heading text-sm font-medium text-[#171717]">Signature Preview</h2>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPreviewMode("resolved")} data-testid="preview-mode-resolved"
                className={`px-2.5 py-1 text-[11px] rounded-sm border transition-colors ${previewMode === "resolved" ? "bg-[#0F6CBD] text-white border-[#0F6CBD]" : "bg-white text-[#5E5E5E] border-[#E1DFDD]"}`}>
                Rendered
              </button>
              <button onClick={() => setPreviewMode("raw")} data-testid="preview-mode-raw"
                className={`px-2.5 py-1 text-[11px] rounded-sm border transition-colors ${previewMode === "raw" ? "bg-[#0F6CBD] text-white border-[#0F6CBD]" : "bg-white text-[#5E5E5E] border-[#E1DFDD]"}`}>
                Raw Tokens
              </button>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Users size={13} className="text-[#5E5E5E]" />
            <select value={selectedUser} onChange={e => loadPreview(e.target.value)}
              className="text-xs border border-[#E1DFDD] rounded-sm px-2 py-1.5 bg-white text-[#171717] flex-1 max-w-sm"
              data-testid="preview-user-select">
              <option value="">Select a user from directory...</option>
              {users.map(u => (
                <option key={u.mail} value={u.mail}>{u.displayName} ({u.mail})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-[#E1DFDD]">
          {/* Full */}
          <div>
            <div className="px-4 py-2 border-b border-[#E1DFDD] flex items-center justify-between bg-[#FAFAFA]">
              <span className="text-xs font-medium text-[#171717]">Full Signature</span>
              <CopyBtn text={preview ? preview.full_html_resolved || preview.full_html_raw : data.full_signature_html} label="Full HTML" />
            </div>
            <div className="p-4" data-testid="full-preview">
              {previewMode === "raw" ? (
                <pre className="text-[10px] font-mono text-[#5E5E5E] whitespace-pre-wrap break-all">{data.full_signature_html}</pre>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: _getResolvedHtml(preview, "full", data.full_signature_html) }} />
              )}
            </div>
          </div>
          {/* Short */}
          <div>
            <div className="px-4 py-2 border-b border-[#E1DFDD] flex items-center justify-between bg-[#FAFAFA]">
              <span className="text-xs font-medium text-[#171717]">Short Signature</span>
              <CopyBtn text={preview ? preview.short_html_resolved || preview.short_html_raw : data.short_signature_html} label="Short HTML" />
            </div>
            <div className="p-4" data-testid="short-preview">
              {previewMode === "raw" ? (
                <pre className="text-[10px] font-mono text-[#5E5E5E] whitespace-pre-wrap break-all">{data.short_signature_html}</pre>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: _getResolvedHtml(preview, "short", data.short_signature_html) }} />
              )}
            </div>
          </div>
        </div>

        {/* Empty token warnings */}
        {preview?.empty_tokens?.length > 0 && (
          <div className="px-4 py-2 border-t border-[#E1DFDD] bg-[#FFF4CE] text-xs text-[#5E5E5E]" data-testid="empty-token-warning">
            <AlertTriangle size={12} className="inline mr-1 text-[#D83B01]" />
            <span className="font-medium text-[#171717]">Missing Entra fields for {preview.user?.displayName}:</span>{" "}
            {preview.empty_tokens.map(t => t.replace(/%/g, "")).join(", ")}
            <span className="ml-1">— these will appear blank in the signature.</span>
          </div>
        )}
      </div>

      {/* ── Test Checklist ── */}
      <div className="mt-6 bg-white border border-[#E1DFDD] rounded-sm" data-testid="test-checklist">
        <div className="px-5 py-3 border-b border-[#E1DFDD] flex items-center gap-2">
          <ClipboardList size={16} className="text-[#0F6CBD]" />
          <h2 className="font-heading text-sm font-medium text-[#171717]">Post-Deployment Test Checklist</h2>
        </div>
        <div className="p-4 space-y-0">
          {data.test_checklist.map((item) => (
            <div key={item.step} className="flex gap-3 py-2.5 border-b border-[#F3F2F1] last:border-0" data-testid={`checklist-step-${item.step}`}>
              <div className="w-5 h-5 rounded-full border-2 border-[#E1DFDD] shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-[#171717]">
                  <span className="text-[#5E5E5E] mr-1.5">{item.step}.</span>{item.action}
                </p>
                <p className="text-[11px] text-[#5E5E5E] mt-0.5">{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Limitations ── */}
      <div className="mt-4 bg-[#F3F2F1] border border-[#E1DFDD] rounded-sm p-4 text-xs text-[#5E5E5E] mb-8">
        <p className="font-medium text-[#171717] mb-1">Exchange Online native limitations</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Signature is appended — not placed directly under latest reply/forward</li>
          <li>Injected signature does not appear in Sent Items</li>
          <li>Empty fields (%%Phone%% etc.) render as blank — populate Entra data first</li>
          <li>Reply/forward detection uses subject prefix matching (RE:/FW:/AW:/SV:), not true thread memory</li>
        </ul>
      </div>
    </div>
  );
}

/* ── Helper: get resolved HTML for preview ── */
function _getResolvedHtml(preview, type, fallbackHtml) {
  if (preview) {
    return type === "full"
      ? preview.full_html_resolved
      : preview.short_html_resolved;
  }
  // Default sample data when no user selected
  const map = {
    "%%DisplayName%%": "Rok Kobal", "%%Title%%": "CEO",
    "%%Company%%": "Europacific, Logistika d.o.o.", "%%Phone%%": "+386-5-6626-381",
    "%%MobilePhone%%": "+386-40-793-000", "%%Office%%": "Koper",
    "%%WindowsEmailAddress%%": "rok.kobal@europacific.com"
  };
  return fallbackHtml.replace(/%%\w+%%/g, m => map[m] || m);
}

/* ── Reusable Script Section ── */
function ScriptSection({ num, color, icon, title, description, script, expanded, onToggle, copyBtn, danger }) {
  return (
    <div className={`mt-3 bg-white border rounded-sm ${danger ? "border-[#A4262C]/30" : "border-[#E1DFDD]"}`}>
      <div className={`w-full px-5 py-3 flex items-center justify-between ${danger ? "bg-[#A4262C]/5" : ""}`}>
        <button onClick={onToggle} className="flex items-center gap-2"
          data-testid={`toggle-${title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
          {num !== null ? (
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: color }}>{num}</div>
          ) : (
            <ShieldAlert size={16} style={{ color }} />
          )}
          <h2 className="font-heading text-sm font-medium" style={{ color: danger ? "#A4262C" : "#171717" }}>{title}</h2>
          {expanded ? <ChevronUp size={14} className="text-[#5E5E5E] ml-1" /> : <ChevronDown size={14} className="text-[#5E5E5E] ml-1" />}
        </button>
        {expanded && <span>{copyBtn}</span>}
      </div>
      {expanded && (
        <div className="px-5 pb-4 border-t border-[#E1DFDD]">
          <p className="text-xs text-[#5E5E5E] mt-3 mb-3">{description}</p>
          <pre className="bg-[#1E1E1E] text-[#D4D4D4] text-[11px] font-mono p-4 rounded-sm overflow-x-auto max-h-56 whitespace-pre-wrap" data-testid={`script-${num ?? 'rollback'}`}>{script}</pre>
        </div>
      )}
    </div>
  );
}
